import { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Checkbox } from "../../ui/checkbox";
import {
    Plus,
    Edit3,
    Trash2,
    Monitor,
    Laptop,
    Smartphone,
    X,
    Hash,
    Building2,
    Search,
    ChevronLeft,
    ChevronRight,
    FileText,
    Group
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { myLocalStorage } from "../../StorageHelper";
import { addGroupDetails, deleteDeviceGroup, editGroupDetails, fetchComputerForCategory, fetchEndpointGroups } from "./Api/endpointgroupapi";
import { transformApiToDevice } from "../Endpoints/helper";
import { Device } from "../interfaces/EndpointInterfaces";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { PolicyProfiles } from "../PolicyProfiles/interface";
import { getProfiles } from "../PolicyProfiles/Api/policyprofileapi";
import { formatDate } from "../formatTimeAgo";
import { Skeleton } from "../interfaces/CommonInterfaces";
import EmptyStateCard from "../EmptyStateCard";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";
import useUserStore from "../../../services/userStore";



interface DevicePattern {
    id: string;
    pattern: string;
    includedDevices: Device[];
    excludedDevices: Device[];
}

export default function EndpointGroupDetails() {
    const [groups, setGroups] = useState<EndpointGroups[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string | null>("1");
    const [activeTab, setActiveTab] = useState<"windows" | "linux" | "macos">("windows");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [groupsSearch, setGroupsSearch] = useState("");
    const [devicesSearch, setDevicesSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [groupDevices, setGroupDevices] = useState<Device[]>([]);
    const [devices, setDevices] = useState<Device[] | []>([])
    const [liveMatches, setLiveMatches] = useState<any[]>([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedGroupDetails, setSelectedGroupDetails] = useState<EndpointGroups>();
    const { role, email, userId, userRole } = useUserStore((state) => state.user);
    const DESCRIPTION_MAX_LENGTH = 25;


    const groupsPerPage = 6;
    const getDefaultFormData = () => ({
        name: "",
        priority: 10,
        profile: "",
        criteria: "install_tags" as "install_tags" | "organization_unit" | "device_pattern",
        tags: [] as string[],
        organizationUnit: "",
        devicePatterns: [] as DevicePattern[],
        patternInput: "",
        tagInput: "",
        isEdit: false
    });
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";

    const [formData, setFormData] = useState(getDefaultFormData());
    const [selectedPattern, setSelectedPattern] = useState<string>("all");

    const [profiles, setProfiles] = useState<PolicyProfiles[]>([]);

    const [loading, setLoading] = useState(false);

    const { toast } = useToast();

    const hasDevicePattern = formData.devicePatterns.length > 0 || formData.patternInput.trim() !== "";
    const hasTags = formData.tags.length > 0 || formData.tagInput.trim() !== "";
    const hasOrganizationUnit = formData.organizationUnit.trim() !== "";

    const disableInstallTags = (hasDevicePattern || hasOrganizationUnit) && formData.criteria !== "install_tags";
    const disableOrganizationUnit = (hasDevicePattern || hasTags) && formData.criteria !== "organization_unit";
    const disableDevicePattern = (hasTags || hasOrganizationUnit) && formData.criteria !== "device_pattern";

    const filteredGroups = useMemo(() => {
        return groups
            .filter(group => group.operationSystem === activeTab.toUpperCase())
            .filter(group =>
                groupsSearch === "" ||
                group.name.toLowerCase().includes(groupsSearch.toLowerCase()) ||
                group.profileName.toLowerCase().includes(groupsSearch.toLowerCase())
            );
    }, [groups, activeTab, groupsSearch]);

    const totalPages = Math.ceil(filteredGroups.length / groupsPerPage);
    const startIndex = (currentPage - 1) * groupsPerPage;
    const paginatedGroups = filteredGroups.slice(startIndex, startIndex + groupsPerPage);

    useMemo(() => {
        setCurrentPage(1);
    }, [activeTab, groupsSearch]);

    const selectedGroupDevices = useMemo(() => {


        return devicesSearch === ""
            ? groupDevices
            : groupDevices.filter(device =>
                device.name.toLowerCase().includes(devicesSearch.toLowerCase()) ||
                device.os.toLowerCase().includes(devicesSearch.toLowerCase())
            );
    }, [groupDevices, selectedGroup, devicesSearch]);

    const getMatchingDevices = (pattern: string) => {
        return groupDevices.filter(device =>
            device.name.toLowerCase().includes(pattern.toLowerCase())
        );
    };



    const handleCreateGroup = async () => {
        const isDevicePattern = formData.criteria === "device_pattern";
        const isInstallTags = formData.criteria === "install_tags";
        const isOrganizationUnit = formData.criteria === "organization_unit";
        let osType = (activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS") ? "MACOS" : activeTab.toUpperCase();
        let payload = {
            name: formData.name,
            tenantName,
            profileId: -1,
            profileName: formData.profile,
            os: osType,
            reputationScore: formData.priority,
            searchPatterns: isDevicePattern
                ? formData.devicePatterns.map(p => ({
                    id: p.id,
                    text: p.pattern
                }))
                : [],
            manualExclusions: isDevicePattern
                ? formData.devicePatterns.flatMap(p =>
                    p.excludedDevices.map(d => ({
                        id: Number(d.id),
                        name: d.name
                    }))
                )
                : [],
            installTags: isInstallTags ? (formData.tags || []) : [],
            organizationUnit: isOrganizationUnit ? (formData.organizationUnit || "") : ""
        };

        if (formData.isEdit) {
            let profileId = profiles.find(
                p => p.name?.toLowerCase() === formData.profile?.toLowerCase() && p.os?.toUpperCase() === activeTab.toUpperCase()
            )?.id ?? selectedGroupDetails?.profileId ?? -1;
            let osType = (activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS") ? "MACOS" : activeTab.toUpperCase();

            let editpayload = {
                uuid: selectedGroupDetails?.uuid,
                name: selectedGroupDetails?.name,
                newName: formData.name,
                tenantName,
                profileId: profileId,
                profileName: formData.profile,
                os: osType,
                reputationScore: formData.priority,

                oldSearchPatterns: selectedGroupDetails?.searchPatterns || [],

                newSearchPatterns: isDevicePattern
                    ? formData.devicePatterns.map(p => ({
                        id: p.id,
                        text: p.pattern
                    }))
                    : [],

                manualExclusions: isDevicePattern
                    ? formData.devicePatterns.flatMap(p =>
                        p.excludedDevices.map(d => ({
                            id: Number(d.id),
                            name: d.name
                        }))
                    )
                    : [],

                installTags: isInstallTags ? (formData.tags || []) : [],

                ouId: isOrganizationUnit ? (selectedGroupDetails?.organizationalUnitId ?? null) : null,
                organizationUnit: isOrganizationUnit ? (formData.organizationUnit || "") : "",
                endpointIdList: selectedGroupDetails?.endpointsIdList || []
            };

            await editGroupDetails(editpayload);

            setIsCreateDialogOpen(false);
            getDeviceList();
            getEndpointGroups();
            toast({
                title: "Group Updated",
                description: `Endpoint group "${formData.name}" has been updated. Resources might reflect in few minutes.`,
            });

        } else {
            const selectedProfile = profiles.find(
                p => p.name?.toLowerCase() === formData.profile?.toLowerCase() && p.os?.toUpperCase() === activeTab.toUpperCase()
            );

            const profileId = selectedProfile?.id || -1;
            payload = { ...payload, profileId }
            let response = await addGroupDetails(payload);
            if (response) {
                setIsCreateDialogOpen(false);
                getDeviceList();
                getEndpointGroups();
                toast({
                    title: "Group Created",
                    description: `Endpoint group "${formData.name}" has been created. Resources might reflect in few minutes.`,
                });
            }
        }


    };

    const openEditGroup = (group: EndpointGroups) => {
        getComputerGroup(group.name);
        setSelectedGroupDetails(group);
        const updatedFormData = getDefaultEditFormData(group);
        setFormData(updatedFormData);
        setIsCreateDialogOpen(true);

    }

    const getDefaultEditFormData = (data?: EndpointGroups) => ({
        name: data?.name || "",
        priority: data?.reputationScore || 10,
        profile:
            data?.profileName ||
            (profiles.filter((p) => p.name.startsWith(activeTab))[0].name || ""),
        criteria: data
            ? data.searchPatterns?.length > 0
                ? "device_pattern"
                : data.installTags?.length > 0
                    ? "install_tags"
                    : data.organizationalUnitName
                        ? "organization_unit"
                        : "install_tags"
            : ("install_tags" as "install_tags" | "organization_unit" | "device_pattern"),
        tags: data?.installTags || [],
        organizationUnit: data?.organizationalUnitName || "",
        devicePatterns: data?.searchPatterns?.map((pattern) => {
            const excludedDevices = data.manualExclusions?.map((ex) =>
                devices.find((d) => d.id === ex.id.toString())
            ).filter(Boolean) as Device[] || [];

            const includedDevices = devices.filter((device) => {
                const regex = new RegExp(pattern.replace(/\*/g, ".*").replace(/\?/g, "."), "i");
                const matches = regex.test(device.name);
                const isExcluded = excludedDevices.some((d) => d.id === device.id);
                return matches && !isExcluded;
            });

            return {
                id: Date.now().toString() + Math.random().toString(),
                pattern,
                includedDevices,
                excludedDevices
            };
        }) || [],
        patternInput: "",
        tagInput: "",
        isEdit: true
    });

    const handleDeleteGroup = (groupId: string, groupName: string, group: EndpointGroups) => {
        setSelectedGroupDetails(group);
        setShowDeleteConfirmation(true);

    };
    const handleDelete = async (group: EndpointGroups) => {
        setGroups(groups.filter(g => g.uuid !== group.uuid));
        if (selectedGroup === group.uuid) {
            setSelectedGroup(null);
        }
        await deleteDeviceGroup(group.uuid);
        setShowDeleteConfirmation(false);
        getDeviceList();
        getEndpointGroups();
        toast({
            title: "Group Deleted",
            description: `Endpoint group "${group.name}" has been deleted. Resources might reflect in few minutes.`,
        });
    }

    const addTag = () => {
        if (formData.tagInput.trim() && !formData.tags.includes(formData.tagInput.trim())) {
            setFormData({
                ...formData,
                tags: [...formData.tags, formData.tagInput.trim()],
                tagInput: ""
            });
        }
    };

    const removeTag = (tagToRemove: string) => {
        setFormData({
            ...formData,
            tags: formData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addTag();
        }
    };

    const getMatchingPattern = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setFormData({
            ...formData,
            patternInput: value,
        });

        if (!value.trim()) {
            setLiveMatches([]);
            return;
        }

        const regexPattern = value.replace(/\*/g, ".*").replace(/\?/g, ".");
        try {
            const regex = new RegExp(regexPattern, "i");
            const matches = devices.filter((device) => regex.test(device.name) && !device.isManuallyAddedMachine);
            setLiveMatches(matches);
        } catch (error) {
            console.warn("Invalid pattern:", error);
            setLiveMatches([]);
        }
    };


    const addPattern = () => {
        if (formData.patternInput.trim()) {
            const matchingDevices = liveMatches; // use live preview devices

            const newPattern: DevicePattern = {
                id: Date.now().toString(),
                pattern: formData.patternInput.trim(),
                includedDevices: matchingDevices,
                excludedDevices: [],
            };

            setFormData({
                ...formData,
                devicePatterns: [...formData.devicePatterns, newPattern],
                patternInput: "",
            });

            setLiveMatches([]);
        }
    };

    const removePattern = (patternId: string) => {
        const patternToRemove = formData.devicePatterns.find(p => p.id === patternId);
        setFormData({
            ...formData,
            devicePatterns: formData.devicePatterns.filter(p => p.id !== patternId)
        });
        if (selectedPattern === patternToRemove?.pattern) {
            setSelectedPattern("all");
        }
    };

    const toggleDeviceInPattern = (patternId: string, device: Device) => {
        setFormData((prev) => ({
            ...prev,
            devicePatterns: prev.devicePatterns.map((pattern) => {
                if (pattern.id === patternId) {
                    const isIncluded = pattern.includedDevices.some((d) => d.id === device.id);

                    if (isIncluded) {
                        return {
                            ...pattern,
                            includedDevices: pattern.includedDevices.filter((d) => d.id !== device.id),
                            excludedDevices: [
                                ...pattern.excludedDevices.filter((d) => d.id !== device.id),
                                device,
                            ],
                        };
                    } else {
                        return {
                            ...pattern,
                            includedDevices: [
                                ...pattern.includedDevices.filter((d) => d.id !== device.id),
                                device,
                            ],
                            excludedDevices: pattern.excludedDevices.filter((d) => d.id !== device.id),
                        };
                    }
                }
                return pattern;
            }),
        }));
    };


    const includeAllDevicesInPattern = (patternId: string) => {
        const pattern = formData.devicePatterns.find((p) => p.id === patternId);
        if (pattern) {
            const matchingDevices = getMatchingDevices(pattern.pattern); // returns Device[]

            setFormData((prev) => ({
                ...prev,
                devicePatterns: prev.devicePatterns.map((p) =>
                    p.id === patternId
                        ? {
                            ...p,
                            includedDevices: matchingDevices,
                            excludedDevices: p.excludedDevices.filter(
                                (d) => !matchingDevices.some((m) => m.id === d.id)
                            ),
                        }
                        : p
                ),
            }));
        }
    };


    const removeAllDevicesInPattern = (patternId: string) => {
        setFormData({
            ...formData,
            devicePatterns: formData.devicePatterns.map(p =>
                p.id === patternId
                    ? { ...p, includedDevices: [] }
                    : p
            )
        });
    };

    const getOSIcon = (osType: string) => {
        switch (osType) {
            case "windows": return <FaWindows className="w-6 h-6" />;
            case "linux": return <FaLinux className="w-6 h-6 text-black dark:text-white" />;
            case "mac": return <FaApple className="w-6 h-6 text-gray-700 dark:text-gray-300" />;
            default: return <FaLinux className="w-6 h-6 text-black dark:text-white" />;
        }
    };



    const getComputerGroup = async (categoryName: string) => {
        try {


            const filteredDevices = devices.filter(device =>
                (device.categories ?? []).some(
                    cat => cat.categoryName.toLowerCase() === categoryName.toLowerCase()
                )
            );

            setGroupDevices(filteredDevices as Device[]);
        } catch (error) {
            console.error("Error fetching computer group:", error);
            return [];
        }
    };

    const getEndpointGroups = async (isFirst = false) => {
        setLoading(isFirst);
        let response: EndpointGroups[] = await fetchEndpointGroups(activeTab, tenantName);
        setGroups(response);
        setLoading(false);
    }

    const getDeviceList = async () => {
        const response = await fetchComputerForCategory(activeTab, tenantName);

        const devices = transformApiToDevice(response);
        const filteredDevices = devices.filter(device => !device.isManuallyAddedMachine);

        setDevices(filteredDevices);
    }

    const fetchProfiles = async (tenantName: string) => {
        let response: PolicyProfiles[] = await getProfiles(tenantName);
        if (response) {
            setProfiles(response);
        }
    }

    const normalizeOS = (os?: string) => {
        if (!os) return '';

        const value = os.toLowerCase();

        if (value === 'macos') return 'mac';
        return value;
    };


    useEffect(() => {
        getDeviceList();
        getEndpointGroups(true);

    }, [activeTab])
    useEffect(() => {
        if (tenantName)
            fetchProfiles(tenantName);
    }, [tenantName])


    return (
        <div className="space-y-4 p-5">


            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center border border-purple-200 dark:border-purple-700">
                        <Group className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                            Endpoint Groups
                        </h1>
                        <p className="text-muted-foreground">
                            Organize and manage devices by creating endpoint groups based on different criteria
                        </p>
                    </div>
                </div>

                <Button
                    onClick={() => {
                        setFormData(getDefaultFormData());
                        setIsCreateDialogOpen(true)
                    }}
                    size="sm"
                    data-testid="button-create-group"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create new group
                </Button>
            </div>

            <Tabs value={activeTab} onValueChange={(value) => {
                setActiveTab(value as any);
                setFormData(getDefaultFormData());
                setIsCreateDialogOpen(false);
                setSelectedPattern("all");
                setSelectedGroup(null);
                setGroupsSearch("");
                setDevicesSearch("");
                setCurrentPage(1);
                setGroupDevices([])
            }} className="w-full">
                <div className="flex items-center justify-center mb-2">
                    <TabsList className="grid w-auto grid-cols-3 h-11">


                        <TabsTrigger
                            value="windows"
                            className="flex items-center gap-2 px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium"
                            data-testid="tab-windows"
                        >
                            <FaWindows className=" w-5 h-5 data-[state=active]:text-white" />
                            WINDOWS
                        </TabsTrigger>

                        <TabsTrigger
                            value="linux"
                            className="flex items-center gap-2 px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium"
                            data-testid="tab-linux"
                        >
                            <FaLinux className=" w-5 h-5 text-black dark:text-white" />
                            LINUX
                        </TabsTrigger>
                        <TabsTrigger
                            value="macos"
                            className="flex items-center gap-2 px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium"
                            data-testid="tab-mac"
                        >
                            <FaApple className=" w-5 h-5 text-gray-700 dark:text-gray-300" />
                            MAC
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value={activeTab} className="mt-2">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Groups Table */}
                        <Card>
                            <CardHeader className="py-3 px-4">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        {getOSIcon(activeTab)}
                                        Endpoint Groups ({filteredGroups.length})
                                    </CardTitle>
                                    <div className="w-64">
                                        <Input
                                            placeholder="Search groups..."
                                            value={groupsSearch}
                                            onChange={(e) => setGroupsSearch(e.target.value)}
                                            className="h-8"
                                            data-testid="input-search-groups"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4">
                                {loading ? <><div className="space-y-2 flex-1 overflow-auto">
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <Skeleton key={index} className="w-full h-8 rounded" />
                                    ))}
                                </div></> : paginatedGroups.length > 0 ? (
                                    <div className="grid gap-2">
                                        {paginatedGroups.map((group) => (
                                            <Card
                                                key={group.uuid}
                                                className={`cursor-pointer hover-elevate ${selectedGroup === group.uuid
                                                    ? "border-l-4 border-l-primary bg-primary/5 ring-2 ring-primary/30"
                                                    : "border-l-4"
                                                    }`}
                                                onClick={() => {
                                                    setSelectedGroup(group.uuid)
                                                    getComputerGroup(group.name)
                                                }}
                                                data-testid={`card-group-${group.uuid}`}
                                            >
                                                <CardContent className="p-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 text-primary font-bold text-sm">
                                                                {group.reputationScore}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-foreground text-sm">{group.name}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                                    <span>{group.profileName}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <Badge variant="secondary" className="text-xs px-2 py-0 h-5">
                                                                        {formatDate(group?.createdAt)}
                                                                    </Badge>
                                                                </div>

                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 w-6 p-0"
                                                                data-testid={`button-edit-${group.uuid}`}
                                                                onClick={() => openEditGroup(group)}
                                                            >
                                                                <Edit3 className="w-3 h-3" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteGroup(group.uuid, group.name, group);
                                                                }}
                                                                className="h-6 w-6 p-0 text-destructive"
                                                                data-testid={`button-delete-${group.uuid}`}
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-between pt-4 border-t">
                                                <div className="text-sm text-muted-foreground">
                                                    Showing {startIndex + 1}-{Math.min(startIndex + groupsPerPage, filteredGroups.length)} of {filteredGroups.length} groups
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                        data-testid="button-prev-page"
                                                    >
                                                        <ChevronLeft className="w-4 h-4" />
                                                    </Button>
                                                    <span className="text-sm font-medium">
                                                        {currentPage} of {totalPages}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                        data-testid="button-next-page"
                                                    >
                                                        <ChevronRight className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <EmptyStateCard
                                        icon={Group}
                                        title={`No endpoint groups for ${activeTab.toUpperCase()}`}
                                        description="Create your first endpoint group to organize devices by tags or patterns."
                                    />
                                )}
                            </CardContent>
                        </Card>

                        {/* Devices Display */}
                        <Card>
                            <CardHeader className="py-3 px-4">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Monitor className="w-4 h-4" />
                                        Devices ({selectedGroupDevices.length})
                                    </CardTitle>
                                    <div className="w-64">
                                        <Input
                                            placeholder="Search devices..."
                                            value={devicesSearch}
                                            onChange={(e) => setDevicesSearch(e.target.value)}
                                            className="h-8"
                                            data-testid="input-search-devices"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4">
                                {selectedGroupDevices.length > 0 ? (
                                    <div className="grid gap-2">
                                        {selectedGroupDevices.map((device, index) => (
                                            <Card
                                                key={device.id}
                                                className="cursor-pointer hover-elevate border-l-4 border-l-accent/40"
                                                data-testid={`card-device-${device.id}`}
                                            >
                                                <CardContent className="p-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-accent/10 text-accent font-medium text-xs">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <Monitor className="w-3 h-3 text-muted-foreground" />
                                                                    <span className="font-medium text-foreground text-sm">{device.name}</span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    {device.os}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="secondary" className="text-xs h-5 px-2">
                                                                Online
                                                            </Badge>
                                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyStateCard
                                        icon={Monitor}
                                        title={selectedGroup ? "No devices in this group" : "No group selected"}
                                        description={
                                            selectedGroup
                                                ? "No devices match your search in this endpoint group."
                                                : "Select an endpoint group from the list to view its devices."
                                        }
                                        compact
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Create Group Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Create New Endpoint Group
                        </DialogTitle>
                        <DialogDescription>
                            Create a new endpoint group for {activeTab.toUpperCase()} devices
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Group Name and Priority */}
                        <div className="flex gap-3">
                            <div className="flex-1 space-y-1">
                                <Label htmlFor="group-name" className="text-xs font-medium text-muted-foreground">
                                    Group Name *
                                </Label>
                                <Input
                                    id="group-name"
                                    value={formData.name}
                                    maxLength={DESCRIPTION_MAX_LENGTH}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, DESCRIPTION_MAX_LENGTH) })}
                                    placeholder="e.g., desktop-machines"
                                    className="h-8"
                                    data-testid="input-group-name"
                                />
                                <p className="text-[10px] text-muted-foreground text-right">{formData.name.length}/{DESCRIPTION_MAX_LENGTH}</p>

                            </div>
                            <div className="w-24 space-y-1">
                                <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                                    Priority *
                                </Label>
                                <Input
                                    id="priority"
                                    type="number"
                                    min={1}
                                    max={100}
                                    step={1}
                                    className="h-8"
                                    value={formData.priority.toString()}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setFormData({
                                            ...formData,
                                            priority: Number.isFinite(value) ? Math.min(Math.max(value, 1), 100) : 1
                                        });
                                    }}
                                    placeholder="10"
                                    data-testid="input-priority"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">Select Profile *</Label>
                            <Select value={formData.profile} onValueChange={(value) => setFormData({ ...formData, profile: value })}>
                                <SelectTrigger className="h-8" data-testid="select-profile">
                                    <SelectValue placeholder={`Select profile for ${activeTab.toLowerCase()}`} />
                                </SelectTrigger>
                                <SelectContent>
                                    {profiles
                                        .filter(profile => normalizeOS(profile?.os) === normalizeOS(activeTab))
                                        .map((profile) => (
                                            <SelectItem key={profile.name} value={profile.name}>
                                                {profile.name}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Group Criteria */}
                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">Select Group Criteria</Label>
                            <RadioGroup
                                value={formData.criteria}
                                onValueChange={(value) => setFormData({ ...formData, criteria: value as any })}
                                className="flex gap-6"
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                        value="install_tags"
                                        id="install_tags"
                                        data-testid="radio-install-tags"
                                        disabled={disableInstallTags}
                                    />
                                    <Label htmlFor="install_tags" className="text-sm flex items-center gap-1">
                                        <Hash className="w-3 h-3" />
                                        Install Tags
                                    </Label>
                                </div>
                                {activeTab === "windows" && (
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem
                                            value="organization_unit"
                                            id="organization_unit"
                                            data-testid="radio-org-unit"
                                            disabled={disableOrganizationUnit}
                                        />
                                        <Label htmlFor="organization_unit" className="text-sm flex items-center gap-1">
                                            <Building2 className="w-3 h-3" />
                                            Organization Unit
                                        </Label>
                                    </div>
                                )}
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                        value="device_pattern"
                                        id="device_pattern"
                                        data-testid="radio-device-pattern"
                                        disabled={disableDevicePattern}
                                    />
                                    <Label htmlFor="device_pattern" className="text-sm flex items-center gap-1">
                                        <Search className="w-3 h-3" />
                                        Device Pattern
                                    </Label>
                                </div>
                            </RadioGroup>
                        </div>

                        {/* Criteria-specific fields */}
                        {formData.criteria === "install_tags" && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Tags</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={formData.tagInput}
                                        onChange={(e) => setFormData({ ...formData, tagInput: e.target.value })}
                                        onKeyDown={handleTagInputKeyDown}
                                        placeholder="Add Tag"
                                        className="flex-1"
                                        data-testid="input-tag"
                                    />
                                    <Button type="button" variant="outline" onClick={addTag} data-testid="button-add-tag">
                                        Add
                                    </Button>
                                </div>
                                {formData.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {formData.tags.map((tag, index) => (
                                            <Badge key={index} variant="default">
                                                {tag}
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeTag(tag)}
                                                    className="ml-1 h-auto p-0"
                                                    data-testid={`button-remove-tag-${index}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "windows" && formData.criteria === "organization_unit" && (
                            <div className="space-y-2">
                                <Label htmlFor="org-unit" className="text-sm font-medium">
                                    Organization Unit
                                </Label>
                                <Input
                                    id="org-unit"
                                    value={formData.organizationUnit}
                                    onChange={(e) => setFormData({ ...formData, organizationUnit: e.target.value })}
                                    placeholder="Enter organization unit"
                                    data-testid="input-org-unit"
                                />
                            </div>
                        )}

                        {formData.criteria === "device_pattern" && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-primary">Pattern</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Enter pattern"
                                            data-testid="input-pattern"
                                            className="border-2 border-primary/20 focus:border-primary/40"
                                            value={formData.patternInput}
                                            onChange={(e) => getMatchingPattern(e)}
                                            onKeyDown={(e) => e.key === 'Enter' && addPattern()}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={addPattern}
                                            className="px-6"
                                            data-testid="button-add-pattern"
                                        >
                                            Add pattern
                                        </Button>
                                    </div>
                                </div>

                                {/* Show patterns as pills when added */}
                                {formData.devicePatterns.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.devicePatterns.map((pattern) => (
                                            <Badge
                                                key={pattern.id}
                                                variant={selectedPattern === pattern.pattern ? "default" : "secondary"}
                                                className={`px-3 py-1 text-sm flex items-center gap-1 cursor-pointer hover-elevate ${selectedPattern === pattern.pattern ? "bg-primary text-primary-foreground" : ""
                                                    }`}
                                                onClick={() => setSelectedPattern(selectedPattern === pattern.pattern ? "" : pattern.pattern)}
                                                data-testid={`badge-pattern-${pattern.id}`}
                                            >
                                                {pattern.pattern}
                                                <X
                                                    className="w-3 h-3 cursor-pointer hover:text-red-500"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removePattern(pattern.id);
                                                    }}
                                                    data-testid={`button-remove-pattern-${pattern.id}`}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                {/* Matched Devices */}
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs font-medium text-muted-foreground">Matched Devices</Label>
                                        {formData.devicePatterns.length > 0 && selectedPattern && (
                                            <div className="flex gap-1">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => {
                                                        const pattern = formData.devicePatterns.find((p) => p.pattern === selectedPattern);
                                                        if (pattern) includeAllDevicesInPattern(pattern.id);
                                                    }}
                                                >
                                                    Include all
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => {
                                                        const pattern = formData.devicePatterns.find((p) => p.pattern === selectedPattern);
                                                        if (pattern) removeAllDevicesInPattern(pattern.id);
                                                    }}
                                                >
                                                    Remove all
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="border rounded-md bg-background">
                                        <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-muted/50 font-medium text-xs border-b">
                                            <div className="col-span-10">Device</div>
                                            <div className="col-span-2">Included</div>
                                        </div>

                                        <div className="max-h-40 overflow-y-auto">
                                            {/* If typing new pattern, show live matches */}
                                            {formData.patternInput && liveMatches.length > 0 ? (
                                                liveMatches.map((device) => (
                                                    <div
                                                        key={`live-${device.id}`}
                                                        className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b text-xs last:border-b-0 hover:bg-muted/20"
                                                    >
                                                        <div className="col-span-10 font-medium break-all flex items-center gap-2">
                                                            {device.os.toLowerCase().includes("server") ? (
                                                                <Monitor className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                            ) : device.name.toLowerCase().includes("laptop") ||
                                                                device.name.toLowerCase().includes("phany") ? (
                                                                <Laptop className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                            ) : (
                                                                <Smartphone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                            )}
                                                            <span className="truncate">{device.name}</span>
                                                        </div>
                                                        <div className="col-span-2 text-center text-muted-foreground italic">-</div>
                                                    </div>
                                                ))
                                            ) : selectedPattern ? (
                                                formData.devicePatterns
                                                    .filter((pattern) => pattern.pattern === selectedPattern)
                                                    .flatMap((pattern) =>
                                                        pattern.includedDevices.map((device) => (
                                                            <div
                                                                key={`${pattern.id}-${device.id}`}
                                                                className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b text-xs last:border-b-0 hover:bg-muted/20"
                                                            >
                                                                <div className="col-span-10 font-medium break-all flex items-center gap-2">
                                                                    {device.os.toLowerCase().includes("server") ? (
                                                                        <Monitor className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                    ) : device.name.toLowerCase().includes("laptop") ||
                                                                        device.name.toLowerCase().includes("phany") ? (
                                                                        <Laptop className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                    ) : (
                                                                        <Smartphone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                    )}
                                                                    <span className="truncate">{device.name}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <Checkbox
                                                                        checked={pattern.includedDevices.some((d) => d.id === device.id)}
                                                                        onCheckedChange={() => toggleDeviceInPattern(pattern.id, device)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))
                                                    )
                                            ) : (
                                                <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                                                    {formData.devicePatterns.length > 0
                                                        ? "Select a pattern to view its devices"
                                                        : "Add patterns or type to see matched devices"}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                            data-testid="button-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateGroup}
                            disabled={
                                !formData.name.trim() ||
                                !formData.profile ||
                                (formData.criteria === "device_pattern" && formData.devicePatterns.length === 0)
                            }
                            data-testid="button-submit"
                        >
                            {formData.isEdit ? "Edit" : "Submit"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                open={showDeleteConfirmation}
                onOpenChange={setShowDeleteConfirmation}
                title="Delete Category"
                description={`Are you sure, you want to delete this group - ${selectedGroupDetails?.name}?`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
                onConfirm={() => {
                    if (selectedGroupDetails)
                        handleDelete(selectedGroupDetails);
                }}
            />
        </div>
    );
}